import React, { useState, useEffect, memo, useMemo, useCallback } from 'react';
import { formatCredits } from '../../../utils/numberFormat';
import {
  Grid,
  Paper,
  Typography,
  Box,
  Card,
  CardContent,
  Button,
  Avatar,
  Chip,
  LinearProgress,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  IconButton,
  Divider,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  CircularProgress,
  Tooltip
} from '@mui/material';
import {
  AccountBalance,
  TrendingUp,
  Image as ImageIcon,
  VideoLibrary,
  Gif,
  Add,
  Refresh,
  Download,
  Star,
  Timeline,
  CreditCard,
  CheckCircle,
  Schedule,
  Error,
  Assignment,
  Close,
  Info,
  Cancel,
  PlayArrow
} from '@mui/icons-material';
import { useMerchantAuth } from '../../../contexts/MerchantAuthContext';
import { merchantDashboardAPI, aiGenerationAPI, creditsAPI, merchantModelFaceAPI, merchantAuthAPI } from '../../../services/merchantAPI';
import { masterDataConfigAPI } from '../../../services/api';
import { useNavigate } from 'react-router-dom';
import { API_URLS } from '../../../config/apiConfig';
import FlutterServicesAPI from '../../../services/flutterServices';
import Cookies from 'js-cookie';
import { STATUS_COLORS } from '../../../utils/statusColors';
import { getThumbnailUrl as getSecureThumbnailUrl, getModelFaceThumbnailUrl } from '../../../utils/imageUrlUtils';
import {
  getGenerationDownloadBaseName,
  getGenerationDownloadFileName
} from '../../../utils/generationDownloadFilename';
import SecureImage from '../../Common/SecureImage';
import { useMerchantPermissions } from '../../../contexts/MerchantPermissionContext';

const MerchantDashboardContent = ({ hideWelcomeSection }) => {
  const [stats, setStats] = useState(null);
  const [creditsInfo, setCreditsInfo] = useState(null);
  const [recentGenerations, setRecentGenerations] = useState([]);
  const [rawModels, setRawModels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [purchaseDialog, setPurchaseDialog] = useState(false);
  const [selectedPackage, setSelectedPackage] = useState(null);
  const [purchaseLoading, setPurchaseLoading] = useState(false);
  const [purchaseError, setPurchaseError] = useState('');
  const [successDialog, setSuccessDialog] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [requestDetails, setRequestDetails] = useState(null);
  const [customRequestDialog, setCustomRequestDialog] = useState(false);
  const { hasPermission, loading: permissionLoading } = useMerchantPermissions();
  const [customRequest, setCustomRequest] = useState({
    credits: '',
    notes: ''
  });
  const [maxCreditLimit, setMaxCreditLimit] = useState(10000000); // Default fallback for MAX_CREDIT_ALLOCATION_LIMIT
  const [validationError, setValidationError] = useState('');

  const { merchant, refreshProfile } = useMerchantAuth();
  const navigate = useNavigate();
  const [showWhatsAppSmsAuth, setShowWhatsAppSmsAuth] = useState(true);

  // Check auth visibility on mount
  useEffect(() => {
    const checkAuthVisibility = async () => {
      try {
        const response = await merchantAuthAPI.checkAuthVisibility();
        if (response.data && typeof response.data.show_whatsapp_sms_auth === 'boolean') {
          setShowWhatsAppSmsAuth(response.data.show_whatsapp_sms_auth);
        }
      } catch (error) {
        console.error('❌ MerchantDashboardContent: Failed to check auth visibility:', error);
      }
    };
    checkAuthVisibility();
  }, []);

  const canPurchaseCredits = hasPermission('Credits Management', 'request_tokens');
  const canViewHistory = hasPermission('Generation History', 'view');
  const canDownload = hasPermission('Generation History', 'download');
  const canPreview = hasPermission('Generation History', 'preview');
  const canGenerate = hasPermission('AI Generation', 'generate_image');
  const canViewAIModelStudio = hasPermission('AI Model Studio', 'view');

  // Memoized computation for active models filter
  const activeModels = useMemo(() => {
    const nowMs = Date.now();
    return rawModels.filter((m) => {
      const exp = m?.access_expires_at;
      if (!exp) return true;
      const t = Date.parse(exp);
      return !Number.isNaN(t) && t > nowMs;
    });
  }, [rawModels]);

  useEffect(() => {
    loadDashboardData();
  }, []);

  const loadDashboardData = async () => {
    setLoading(true);
    try {
      const [statsRes, creditsRes, generationsRes, modelsRes] = await Promise.all([
        merchantDashboardAPI.getStats(),
        merchantDashboardAPI.getCreditsInfo(),
        merchantDashboardAPI.getRecentGenerations(5),
        merchantModelFaceAPI.listAssignedModels().catch(() => ({ data: { data: [] } }))
      ]);

      setStats(statsRes.data.stats);
      setCreditsInfo(creditsRes.data.credits);
      setRecentGenerations(generationsRes.data.generations);

      const rows = modelsRes?.data?.data || [];
      const arr = Array.isArray(rows) ? rows : [];
      setRawModels(arr);


      await refreshProfile();

      // Fetch system settings for credit limits
      try {
        const settingsResponse = await masterDataConfigAPI.getPublicSystemSettings();
        const settings = settingsResponse.data.settings;
        if (settings.MAX_CREDIT_ALLOCATION_LIMIT) {
          setMaxCreditLimit(settings.MAX_CREDIT_ALLOCATION_LIMIT);
        }
      } catch (error) {
        console.error('Failed to fetch system settings:', error);
        // Keep default value if fetch fails
      }
    } catch (error) {
      console.error('Failed to load dashboard data:', error);
      setError('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status) => {
    const statusValue = typeof status === 'string' ? status : status?.status;
    if (!statusValue || typeof statusValue !== 'string') {
      return 'default';
    }
    switch (statusValue.toLowerCase()) {
      case 'completed': return 'success';
      case 'processing': return 'primary';
      case 'pending': return 'warning';
      case 'failed': return 'error';
      default: return 'default';
    }
  };

  const getFormatIcon = useCallback((format) => {
    const formatValue = typeof format === 'string' ? format : format?.name || format?.display_name;
    if (!formatValue || typeof formatValue !== 'string') {
      return <ImageIcon />;
    }
    switch (formatValue.toLowerCase()) {
      case 'video': return <VideoLibrary />;
      case 'gif': return <Gif />;
      default: return <ImageIcon />;
    }
  }, []);

  const getFileExtension = (outputFormat, generation) => {
    // generated_video_url is the most reliable signal — it's only set when a video file exists
    if (generation?.generated_video_url) return 'mp4';

    const formatValue = typeof outputFormat === 'string' ? outputFormat : outputFormat?.name;
    if (!formatValue || typeof formatValue !== 'string') return 'jpg';

    switch (formatValue.toUpperCase()) {
      case 'VIDEO': return 'mp4';
      case 'GIF': return 'gif';
      default: return 'jpg';
    }
  };

  const handleDownload = useCallback(async (generation) => {
    try {
      // Check if running in Flutter app (mobile)
      if (FlutterServicesAPI.isFlutterApp()) {
        // Get access token for authentication
        const accessToken = Cookies.get('merchant_access_token');
        if (!accessToken) {
          console.error("No access token found for mobile download");
          return;
        }

        // Always use the POST endpoint for downloads to ensure proper authentication
        const downloadUrl = `${API_URLS.BACKEND_BASE}/api/merchant/ai-generation/download/${generation.id}`;

        const downloadData = {
          generationId: generation.id,
          fileType: generation.output_format?.name || 'IMAGE',
          fileName: getGenerationDownloadBaseName(generation),
          downloadUrl: downloadUrl,
          fileExtension: getFileExtension(generation.output_format, generation),
          method: 'POST', // Always use POST for the download endpoint
          headers: {
            'Authorization': `Bearer ${accessToken}`,
            'Content-Type': 'application/json'
          },
          accessToken: accessToken // Pass token separately for Flutter bridge
        };

        // Call mobile download handler with permission check and Flutter bridge
        try {
          await FlutterServicesAPI.downloadFileToStorage(downloadData);
          // Download initiated successfully
        } catch (mobileError) {
          console.error("Mobile download error:", mobileError);
          // Don't show error if download might have succeeded
          // Many Flutter bridges don't return proper responses but still download successfully
        }
      } else {
        // Web download - existing logic (unchanged)
        const response = await aiGenerationAPI.downloadGeneration(generation.id);
        const blob = new Blob([response.data]);
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.style.display = 'none';
        a.href = url;
        a.download = getGenerationDownloadFileName(
          generation,
          getFileExtension(generation.output_format, generation)
        );
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);

        // Refresh the dashboard data to update download count
        // loadDashboardData();
      }
    } catch (error) {
      console.error('Download failed:', error);
    }
  }, []);

  const handlePurchasePackage = useCallback((pkg) => {
    setSelectedPackage(pkg);
    setPurchaseDialog(true);
    setPurchaseError('');
  }, []);

  const confirmPurchase = useCallback(async () => {
    if (!selectedPackage) return;
    try {
      setPurchaseLoading(true);
      setPurchaseError('');
      const response = await creditsAPI.purchaseCredits({ package_id: selectedPackage.id });
      await refreshProfile();
      setPurchaseDialog(false);
      setSelectedPackage(null);
      if (response.data.status === 'PENDING') {
        setSuccessMessage(`${FlutterServicesAPI.getCreditLabel()} request submitted successfully!`);
        setRequestDetails({
          requestId: response.data.request_id,
          status: response.data.status,
          instructions: response.data.instructions,
          packageName: selectedPackage?.name,
          credits: selectedPackage?.credits
        });
      } else {
        setSuccessMessage(`${FlutterServicesAPI.getCreditLabel()} request submitted successfully!`);
        setRequestDetails({
          packageName: selectedPackage?.name,
          credits: selectedPackage?.credits
        });
      }
      setSuccessDialog(true);
    } catch (error) {
      setPurchaseError(`Failed to submit ${FlutterServicesAPI.getCreditLabel().toLowerCase()} request. Please try again.`);
    } finally {
      setPurchaseLoading(false);
    }
  }, [selectedPackage, refreshProfile]);

  const handleCloseSuccessDialog = useCallback(() => {
    setSuccessDialog(false);
    setSuccessMessage('');
    setRequestDetails(null);
  }, []);

  const handleCancelCustomRequest = useCallback(() => {
    setCustomRequestDialog(false);
    setCustomRequest({
      credits: '',
      notes: ''
    });
    setPurchaseError('');
    setValidationError('');
  }, []);

  // Validation function for credit amount
  const validateCreditAmount = (amount) => {
    const numAmount = parseInt(amount);
    if (!numAmount || numAmount < 100) {
      return `Minimum custom ${FlutterServicesAPI.getCreditLabel().toLowerCase()} amount is 100`;
    }
    if (numAmount > maxCreditLimit) {
      return `Maximum allowed is ${maxCreditLimit.toLocaleString()} ${FlutterServicesAPI.getCreditLabel().toLowerCase()}`;
    }
    return null;
  };

  const handleCustomCreditRequest = async () => {
    const validationError = validateCreditAmount(customRequest.credits);
    if (validationError) {
      setPurchaseError(validationError);
      return;
    }

    try {
      setPurchaseLoading(true);
      setPurchaseError('');
      // Submit custom credit request
      const response = await creditsAPI.requestCustomCredits({
        amount: parseInt(customRequest.credits),
        notes: customRequest.notes
      });

      setCustomRequestDialog(false);
      setCustomRequest({
        credits: '',
        notes: ''
      });
      setValidationError('');

      setSuccessMessage(`Custom ${FlutterServicesAPI.getCreditLabel().toLowerCase()} request submitted successfully!`);
      setRequestDetails({
        requestId: response.data?.request_id,
        status: 'PENDING',
        credits: customRequest.credits,
        packageName: `Custom ${FlutterServicesAPI.getCreditLabel()}`
      });
      setSuccessDialog(true);

    } catch (error) {
      console.error('Custom credit request failed:', error);
      setPurchaseError(`Failed to submit custom ${FlutterServicesAPI.getCreditLabel().toLowerCase()} request. Please try again.`);
    } finally {
      setPurchaseLoading(false);
    }
  };

  // Define all possible statuses
  const ALL_GENERATION_STATUSES = [
    { status: 'PROCESSING', display_name: 'Processing', color: 'primary' },
    { status: 'COMPLETED', display_name: 'Completed', color: 'success' },
    { status: 'FAILED', display_name: 'Failed', color: 'error' },
  ];

  if (loading) {
    return (
      <Box sx={{ mt: 4 }}>
        <LinearProgress />
        {/* <Typography variant="h6" sx={{ textAlign: 'center', mt: 2 }}>
          Loading dashboard...
        </Typography> */}
      </Box>
    );
  }

  return (
    <Box>

      <Grid container spacing={3}>
        {/* Available Credits - Mobile Only */}
        <Grid item xs={12} sx={{ display: { xs: 'block', md: 'none' }, height: '50px', mb: 1 }} />
        <Box
          sx={{
            display: { xs: 'block', md: 'none' },
            position: 'fixed',
            top: '73px',
            left: 0,
            width: '100%',
            backgroundColor: '#f5f5f5',
            px: '15px',
            py: '12px',
            zIndex: 1100
          }}
        >          <Box sx={{
          bgcolor: '#FFFFFF',
          border: '1px solid #F1EAFA',
          borderRadius: '13.15px',
          height: '50px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          px: 2,
          boxShadow: '0px 4px 6px rgba(0,0,0,0.02)'
        }}>
            <Typography sx={{ color: '#651BC6', fontFamily: 'var(--font-montserrat-semibold)', fontWeight: 600, fontSize: '17.53px', lineHeight: 1 }}>
              {`Available ${FlutterServicesAPI.getCreditLabel()}`}
            </Typography>
            <Typography sx={{ color: '#651BC6', fontFamily: 'var(--font-montserrat-semibold)', fontWeight: 600, fontSize: '17.53px', lineHeight: 1 }}>
              {merchant?.credits_balance ? formatCredits(merchant.credits_balance) : '0'}
            </Typography>
          </Box>
        </Box>

        {/* Create Visuals CTA */}
        <Grid item xs={12} sm={6} md={3} sx={{ height: '100%', mt: { xs: 0, md: 0 } }}>
          <Card elevation={0} sx={{
            height: { xs: 'auto', sm: 280 },
            display: 'flex',
            flexDirection: 'column',
            border: 'none',
            boxShadow: '0px 4.33px 12.98px 0px rgba(0, 0, 0, 0.08)',
            borderRadius: '17.31px',
            position: 'relative',
            transition: 'transform 0.3s ease-in-out',
            bgcolor: '#fff',
            '&:hover': {
              transform: 'translateY(-2px)',
            }
          }}>
            <CardContent sx={{ flex: 1, display: 'flex', flexDirection: 'column', pb: 3 }}>
              <Box display="flex" alignItems="flex-start" justifyContent="space-between" mb={1}>
                <Typography
                  variant="h6"
                  sx={{
                    fontFamily: 'Montserrat, sans-serif',
                    fontWeight: 600,
                    fontSize: {
                      xs: '17.31px',   // mobile
                      sm: '17.31px',   // 600px+
                      md: '17.31px',
                      lg: '13.31px',
                      xl: '17.31px'    // 900px+
                    },
                    color: '#1E2939',
                    lineHeight: 1.2
                  }}
                >
                  Create Visuals
                </Typography>
                <Box sx={{
                  width: { xs: 64, md: 50 },
                  height: { xs: 64, md: 50 },
                  flexShrink: 0,
                  ml: 1
                }}>
                  <img
                    src="/assets/img/icons/create_visuals.svg"
                    alt="Create Visuals Icon"
                    style={{ width: '100%', height: '100%' }}
                  />
                </Box>
              </Box>
              <Box component="ul" sx={{ pl: 2.5, pr: 1, mt: 0, mb: 'auto', '& li': { fontFamily: 'Montserrat, sans-serif', fontWeight: 400, color: '#000000', fontSize: { xs: '13.68px', md: '12.68px' }, lineHeight: 1.4, mb: 0.4, '&::marker': { color: '#6318C6', fontSize: '1.2em' } } }}>
                <li>Select your preference</li>
                <li>Upload jewelry photo</li>
                <li>Get stunning model photos with your jewelry</li>
              </Box>
              <Box sx={{ mt: 'auto', pt: { xs: 1, sm: 1.5 } }}>
                <Button
                  variant="contained"
                  fullWidth
                  // Primary action for this card: open the generation wizard (not AI model picker — that is the adjacent card).
                  onClick={() => navigate('/generate')}
                  sx={{
                    background: 'linear-gradient(90deg, #A864ED 0%, #F5B760 100%)',
                    color: 'white',
                    fontFamily: 'Montserrat, sans-serif',
                    fontWeight: 600,
                    borderRadius: '10px',
                    py: { xs: 1, sm: 1.2 },
                    textTransform: 'none',
                    fontSize: '14px',
                    boxShadow: 'none',
                    '&:hover': {
                      background: 'linear-gradient(90deg, #9C52DB 0%, #EAA24A 100%)',
                      boxShadow: 'none',
                    },
                  }}
                >
                  Create Visuals
                </Button>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* AI Model Studio — only shown when plan grants AI Model Studio access and not in app review mode */}
        {(permissionLoading || canViewAIModelStudio) && showWhatsAppSmsAuth && <Grid className="ai-model-studio" item xs={12} sm={6} md={3} sx={{ height: '100%', mt: { xs: 0, md: 0 } }}>
          <Card elevation={0} sx={{
            height: { xs: 'auto', sm: 280 },
            display: 'flex',
            flexDirection: 'column',
            border: 'none',
            boxShadow: '0px 4.33px 12.98px 0px rgba(0, 0, 0, 0.08)',
            borderRadius: '17.31px',
            position: 'relative',
            transition: 'transform 0.3s ease-in-out',
            bgcolor: '#fff',
            '&:hover': {
              transform: 'translateY(-2px)',
            }
          }}>
            <CardContent sx={{ flex: 1, display: 'flex', flexDirection: 'column', pb: 3 }}>
              {activeModels.length > 0 ? (
                <>
                  <Box display="flex" alignItems="center" justifyContent="space-between" mb={1.5}>
                    <Typography
                      variant="h6"
                      sx={{
                        fontFamily: 'Montserrat, sans-serif',
                        fontWeight: 600,
                        fontSize: {
                          xs: '17.31px',   // mobile
                          sm: '17.31px',   // 600px+
                          md: '17.31px',
                          lg: '13.31px',
                          xl: '17.31px'    // 900px+
                        },
                        color: '#1E2939',
                        lineHeight: 1.2
                      }}
                    >
                      AI Model Studio
                    </Typography>
                    <Button
                      variant="outlined"
                      disabled={!showWhatsAppSmsAuth && FlutterServicesAPI.isFlutterApp()}
                      onClick={() => {
                        if (showWhatsAppSmsAuth || !FlutterServicesAPI.isFlutterApp()) {
                          navigate('/ai-model-studio');
                        }
                      }}
                      sx={{
                        color: (showWhatsAppSmsAuth || !FlutterServicesAPI.isFlutterApp()) ? '#6318C6' : '#9CA3AF',
                        borderColor: (showWhatsAppSmsAuth || !FlutterServicesAPI.isFlutterApp()) ? '#6318C6' : '#E5E7EB',
                        borderRadius: '20px',
                        textTransform: 'none',
                        fontSize: '14px',
                        px: 2,
                        py: 0.5,
                        minWidth: 'auto',
                        height: '32px',
                        fontFamily: 'Montserrat, sans-serif',
                        fontWeight: 500,
                        '&:hover': {
                          borderColor: showWhatsAppSmsAuth ? '#6318C6' : '#E5E7EB',
                          backgroundColor: showWhatsAppSmsAuth ? 'rgba(99, 24, 198, 0.04)' : 'transparent',
                        },
                        '&.Mui-disabled': {
                          color: '#9CA3AF',
                          borderColor: '#E5E7EB',
                          backgroundColor: 'transparent'
                        }
                      }}
                      >
                        {showWhatsAppSmsAuth ? 'Select More' : 'Coming Soon'}
                      </Button>
                  </Box>

                  <Box display="grid" gridTemplateColumns="repeat(3, 1fr)" mb="auto" px={0.5} mt={0} gap={2}>
                    {activeModels.slice(0, 3).map((model, index) => {
                      const modelName = model.model_name || model.name || 'Model';
                      const modelImage = getModelFaceThumbnailUrl(model.id, 'thumbnail');
                      return (
                        <Box key={index} sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                          <Box sx={{ width: '100%', height: '118px', borderRadius: '10px', overflow: 'hidden', mb: 0.3, border: '1px solid #e0e0e0' }}>
                            <SecureImage
                              src={modelImage}
                              alt={modelName}
                              style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                            />
                          </Box>
                          <Typography sx={{ fontFamily: 'Montserrat, sans-serif', fontSize: '13px', fontWeight: 500, color: '#000', textAlign: 'center' }}>{modelName}</Typography>
                        </Box>
                      );
                    })}
                  </Box>

                  <Box sx={{ mt: 'auto', pt: { xs: 2, sm: 1.5 } }}>
                    <Button
                      variant="contained"
                      fullWidth
                      disabled={!showWhatsAppSmsAuth}
                      onClick={() => {
                        if (showWhatsAppSmsAuth) {
                          navigate('/ai-model-studio');
                        }
                      }}
                      sx={{
                        background: showWhatsAppSmsAuth ? 'linear-gradient(90deg, #A864ED 0%, #F5B760 100%)' : '#E5E7EB',
                        color: showWhatsAppSmsAuth ? 'white' : '#9CA3AF',
                        fontFamily: 'Montserrat, sans-serif',
                        fontWeight: 600,
                        borderRadius: '10px',
                        py: { xs: 1, sm: 1.2 },
                        textTransform: 'none',
                        fontSize: '14px',
                        boxShadow: 'none',
                        '&:hover': {
                          background: showWhatsAppSmsAuth ? 'linear-gradient(90deg, #9C52DB 0%, #EAA24A 100%)' : '#E5E7EB',
                          boxShadow: 'none',
                        },
                        '&.Mui-disabled': {
                          background: '#F3F4F6',
                          color: '#9CA3AF',
                          cursor: 'not-allowed'
                        }
                      }}
                    >
                      {showWhatsAppSmsAuth ? 'Select Your AI Model' : 'Coming Soon'}
                    </Button>
                  </Box>
                </>
              ) : (
                <>
                  <Box display="flex" alignItems="flex-start" justifyContent="space-between" mb={1}>
                    <Typography
                      variant="h6"
                      sx={{
                        fontFamily: 'Montserrat, sans-serif',
                        fontWeight: 600,
                        fontSize: {
                          xs: '17.31px',   // mobile
                          sm: '17.31px',   // 600px+
                          md: '17.31px',
                          lg: '13.31px',
                          xl: '17.31px'    // 900px+
                        },
                        color: '#1E2939',
                        lineHeight: 1.2
                      }}
                    >
                      AI Model Studio
                    </Typography>
                    <Box sx={{
                      width: { xs: 64, md: 58 },
                      height: { xs: 64, md: 58 },
                      flexShrink: 0,
                      ml: 1
                    }}>
                      <img
                        src="/assets/img/icons/ai_model.svg"
                        alt="AI Model Icon"
                        style={{ width: '100%', height: '100%' }}
                      />
                    </Box>
                  </Box>
                  <Box component="ul" sx={{ pl: 2.5, pr: 1, mt: 0, mb: 'auto', '& li': { fontFamily: 'Montserrat, sans-serif', fontWeight: 400, color: '#000000', fontSize: { xs: '13.68px', md: '12.68px' }, lineHeight: 1.4, mb: 0.2, '&::marker': { color: '#6318C6', fontSize: '1.2em' } } }}>
                    <li>Hire An AI Model Instead Of Expensive Photoshoots</li>
                    <li>Create Unlimited Jewellery Campaigns Anytime</li>
                    <li>Keep Your Brand Model Consistent Across Catalogues & Ads</li>
                  </Box>
                  <Box sx={{ mt: 'auto', pt: { xs: 1, sm: 1.5 } }}>
                    <Button
                      variant="contained"
                      fullWidth
                      disabled={!showWhatsAppSmsAuth}
                      onClick={() => {
                        if (showWhatsAppSmsAuth) {
                          navigate('/ai-model-studio');
                        }
                      }}
                      sx={{
                        background: showWhatsAppSmsAuth ? 'linear-gradient(90deg, #A864ED 0%, #F5B760 100%)' : '#E5E7EB',
                        color: showWhatsAppSmsAuth ? 'white' : '#9CA3AF',
                        fontFamily: 'Montserrat, sans-serif',
                        fontWeight: 600,
                        borderRadius: '10px',
                        py: { xs: 1, sm: 1.2 },
                        textTransform: 'none',
                        fontSize: '14px',
                        boxShadow: 'none',
                        '&:hover': {
                          background: showWhatsAppSmsAuth ? 'linear-gradient(90deg, #9C52DB 0%, #EAA24A 100%)' : '#E5E7EB',
                          boxShadow: 'none',
                        },
                        '&.Mui-disabled': {
                          background: '#F3F4F6',
                          color: '#9CA3AF',
                          cursor: 'not-allowed'
                        }
                      }}
                    >
                      {showWhatsAppSmsAuth ? 'Select Your AI Model' : 'Coming Soon'}
                    </Button>
                  </Box>
                </>
              )}
            </CardContent>
          </Card>
        </Grid>}

        {/* Recent Activity */}
        <Grid className="ai-model-studio" item xs={12} sm={6} md={3} sx={{ height: '100%', pt: { xs: 1, md: 2 } }}>
          <Card elevation={0} sx={{
            height: { xs: 'auto', sm: 280 },
            display: 'flex',
            flexDirection: 'column',
            border: 'none',
            boxShadow: '0px 4.33px 12.98px 0px rgba(0, 0, 0, 0.08)',
            borderRadius: '17.31px',
            position: 'relative',
            transition: 'transform 0.3s ease-in-out',
            bgcolor: '#fff',
            '&:hover': {
              transform: 'translateY(-2px)',
            }
          }}>
            <CardContent sx={{ flex: 1, display: 'flex', flexDirection: 'column', pb: 3 }}>
              <Box display="flex" alignItems="flex-start" justifyContent="space-between" mb={1}>
                <Typography
                  variant="h6"
                  sx={{
                    fontFamily: 'Montserrat, sans-serif',
                    fontWeight: 600,
                    fontSize: {
                      xs: '17.31px',   // mobile
                      sm: '17.31px',   // 600px+
                      md: '17.31px',
                      lg: '13.31px',
                      xl: '17.31px'    // 900px+
                    },
                    color: '#1E2939',
                    lineHeight: 1.2
                  }}
                >
                  Recent Activity
                </Typography>
                <Box sx={{
                  width: { xs: 64, md: 50 },
                  height: { xs: 64, md: 50 },
                  flexShrink: 0,
                  ml: 1
                }}>
                  <img
                    src="/assets/img/icons/recent_activity.svg"
                    alt="Recent Activity Icon"
                    style={{ width: '100%', height: '100%' }}
                  />
                </Box>
              </Box>
              <Typography
                variant="h4"
                gutterBottom
                sx={{
                  fontFamily: 'Montserrat, sans-serif',
                  fontWeight: 600,
                  fontSize: { xs: '1.5rem', sm: '2.5rem', md: '3rem' },
                  color: '#6318C6',
                  textAlign: 'left',
                  mb: { xs: 1, md: 2 }
                }}
              >
                {stats?.recent_activity?.last_30_days || 0}
              </Typography>
              <Typography variant="body2" sx={{ fontFamily: 'Montserrat, sans-serif', fontWeight: 400, color: '#8F99A9', fontSize: { xs: '13.68px', md: '12.68px' }, mb: 1 }}>
                Generations in last <span style={{ color: '#000000', fontWeight: 600 }}>30</span> days
              </Typography>
              <Typography variant="body2" sx={{ fontFamily: 'Montserrat, sans-serif', fontWeight: 400, color: '#8F99A9', fontSize: { xs: '13.68px', md: '12.68px' }, mb: 1 }}>
                Total generations: <span style={{ color: '#000000', fontWeight: 600 }}>{stats?.recent_activity?.total_generations || 0}</span>
              </Typography>
              <Box sx={{ mt: 'auto', pt: { xs: 0, sm: 0.7 } }}>
                {canViewHistory && (
                  <Button
                    variant="contained"
                    fullWidth
                    onClick={() => navigate('/history')}
                    sx={{
                      background: 'linear-gradient(90deg, #A864ED 0%, #F5B760 100%)',
                      color: 'white',
                      fontFamily: 'Montserrat, sans-serif',
                      fontWeight: 600,
                      borderRadius: '10px',
                      py: { xs: 1, sm: 1.2 },
                      textTransform: 'none',
                      fontSize: '14px',
                      boxShadow: 'none',
                      '&:hover': {
                        background: 'linear-gradient(90deg, #9C52DB 0%, #EAA24A 100%)',
                        boxShadow: 'none',
                      },
                    }}
                  >
                    View History
                  </Button>
                )}
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Current Month */}
        <Grid className="ai-model-studio" item xs={12} sm={6} md={3} sx={{ height: '100%', pt: { xs: 1, md: 2 } }}>
          <Card elevation={0} sx={{
            height: { xs: 'auto', sm: 280 },
            display: 'flex',
            flexDirection: 'column',
            border: 'none',
            boxShadow: '0px 4.33px 12.98px 0px rgba(0, 0, 0, 0.08)',
            borderRadius: '17.31px',
            position: 'relative',
            transition: 'transform 0.3s ease-in-out',
            bgcolor: '#fff',
            '&:hover': {
              transform: 'translateY(-2px)',
            }
          }}>
            <CardContent sx={{ flex: 1, display: 'flex', flexDirection: 'column', pb: 3 }}>
              <Box display="flex" alignItems="flex-start" justifyContent="space-between" mb={1}>
                <Typography
                  variant="h6"
                  sx={{
                    fontFamily: 'Montserrat, sans-serif',
                    fontWeight: 600,
                    fontSize: {
                      xs: '17.31px',   // mobile
                      sm: '17.31px',   // 600px+
                      md: '17.31px',
                      lg: '13.31px',
                      xl: '17.31px'    // 900px+
                    },
                    color: '#1E2939',
                    lineHeight: 1.2
                  }}
                >
                  Current Month
                </Typography>
                <Box sx={{
                  width: { xs: 64, md: 50 },
                  height: { xs: 64, md: 50 },
                  flexShrink: 0,
                  ml: 1
                }}>
                  <img
                    src="/assets/img/icons/curent_month.svg"
                    alt="Current Month Icon"
                    style={{ width: '100%', height: '100%' }}
                  />
                </Box>
              </Box>
              <Typography
                variant="h4"
                gutterBottom
                sx={{
                  fontFamily: 'Montserrat, sans-serif',
                  fontWeight: 600,
                  fontSize: { xs: '1.5rem', sm: '2.5rem', md: '3rem' },
                  color: '#6318C6',
                  textAlign: 'left',
                  mb: { xs: 1, md: 2 }
                }}
              >
                {stats?.recent_activity?.credits_used_this_month ? formatCredits(stats.recent_activity.credits_used_this_month) : '0'}
              </Typography>
              <Typography variant="body2" sx={{ fontFamily: 'Montserrat, sans-serif', fontWeight: 400, color: '#8F99A9', fontSize: { xs: '13.68px', md: '12.68px' }, mb: 0.5, textTransform: 'capitalize' }}>
                {FlutterServicesAPI.getCreditLabel()} used this month
              </Typography>
              <Typography variant="body2" sx={{ fontFamily: 'Montserrat, sans-serif', fontWeight: 400, color: '#8F99A9', fontSize: { xs: '13.68px', md: '12.68px' } }}>
                Total Generations: <span style={{ color: '#000000', fontWeight: 600 }}>{stats?.recent_activity?.total_generations || 0}</span>
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        {/* Generation Status Overview */}
        <Grid className="ai-model-studio" item xs={12} sm={12} md={12} sx={{ pt: { xs: 1, md: 2 } }}>
          <Card elevation={0} sx={{
            border: 'none',
            boxShadow: '0px 4.33px 12.98px 0px rgba(0, 0, 0, 0.08)',
            borderRadius: '17.31px'
          }}>
            <CardContent>
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                <Typography
                  variant="h6"
                  sx={{
                    fontFamily: 'Montserrat, sans-serif',
                    fontWeight: 600,
                    fontSize: {
                      xs: '17.31px',   // mobile
                      sm: '17.31px',   // 600px+
                      md: '17.31px',
                      lg: '13.31px',
                      xl: '17.31px'    // 900px+
                    },
                    color: '#1E2939'
                  }}
                  mb={2}
                  display="flex"
                  alignItems="center"
                  gap={1}
                >
                  Generation Status
                </Typography>
                <IconButton
                  onClick={loadDashboardData}
                  size="small"
                  sx={{
                    width: 50,
                    height: 50,
                    bgcolor: '#EFEFFF',
                    color: '#6318C6',
                    '&:hover': {
                      bgcolor: '#EFEFFF',
                    },
                    '& .MuiSvgIcon-root': {
                      fontSize: 30,
                    }
                  }}
                >
                  <Refresh />
                </IconButton>
              </Box>

              {/* Horizontal layout for status boxes - 4 boxes in one line on desktop */}
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(3, 1fr)' }, gap: 2 }}>
                {ALL_GENERATION_STATUSES.map((statusObj) => {
                  let count = 0;
                  if (statusObj.status === 'PROCESSING') {
                    // Sum both PROCESSING and PENDING from backend
                    const processingCount = stats?.generation_counts?.find((s) => (s.status || '').toUpperCase() === 'PROCESSING')?.count || 0;
                    const pendingCount = stats?.generation_counts?.find((s) => (s.status || '').toUpperCase() === 'PENDING')?.count || 0;
                    count = processingCount + pendingCount;
                  } else {
                    count = stats?.generation_counts?.find((s) => (s.status || '').toUpperCase() === statusObj.status)?.count || 0;
                  }

                  // Define exact formatting for each status matching the custom spec
                  let statusStyles = {
                    bgcolor: '#FFFBEB',
                    border: '#D97706',
                    color: '#D97706'
                  };
                  if (statusObj.status === 'PROCESSING') {
                    statusStyles = { bgcolor: '#F5F3FF', border: '#7C3AED', color: '#7C3AED' };
                  } else if (statusObj.status === 'COMPLETED') {
                    statusStyles = { bgcolor: '#ECFDF5', border: '#6EE7B7', color: '#059669' };
                  } else if (statusObj.status === 'FAILED') {
                    statusStyles = { bgcolor: '#FEF2F2', border: '#FCA5A5', color: '#DC2626' };
                  }

                  return (
                    <Box
                      key={statusObj.status}
                      sx={{
                        borderRadius: '13.15px',
                        p: { xs: 1.5, sm: 2 },
                        bgcolor: statusStyles.bgcolor,
                        border: `1px solid ${statusStyles.border}`,
                        display: 'flex',
                        flexDirection: 'row',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        minHeight: { xs: '50px', sm: '60px' }
                      }}
                    >
                      <Typography
                        variant="body2"
                        sx={{
                          color: statusStyles.color,
                          fontFamily: 'Montserrat, sans-serif',
                          fontWeight: 500,
                          fontSize: '15.15px'
                        }}
                      >
                        {statusObj.display_name}
                      </Typography>
                      <Typography
                        variant="h4"
                        sx={{
                          color: statusStyles.color,
                          fontFamily: 'Montserrat, sans-serif',
                          fontWeight: 600,
                          fontSize: '22px'
                        }}
                      >
                        {count}
                      </Typography>
                    </Box>
                  );
                })}
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Recent Generations */}
        <Grid className="ai-model-studio" item xs={12} sm={12} md={6} sx={{ pt: { xs: 1, md: 2 } }}>
          <Card elevation={0} sx={{
            border: 'none',
            boxShadow: { xs: 'none', sm: '0px 4.33px 12.98px 0px rgba(0, 0, 0, 0.08)' },
            borderRadius: '17.31px',
            bgcolor: { xs: 'transparent', sm: 'white' },
            minHeight: '100%'
          }}>
            <CardContent sx={{
              p: { xs: 0, sm: 2 },
              '&:last-child': { pb: { xs: 0, sm: 2 } }
            }}>
              <Typography
                variant="h6"
                fontWeight={600}
                fontSize={{
                  xs: '17.31px',   // mobile
                  sm: '17.31px',   // 600px+
                  md: '17.31px',
                  lg: '13.31px',
                  xl: '17.31px'    // 900px+
                }}
                mb={1.5}
                display="flex"
                alignItems="center"
                gap={1}
                color="#374151"
              >
                Recent Generations
              </Typography>
              {recentGenerations.length === 0 ? (
                <Box textAlign="center" py={1.5}>
                  <Typography mb={3} sx={{ fontFamily: 'Montserrat, sans-serif', fontWeight: 400, color: '#000000', fontSize: '12.68px', mb: 3 }}>
                    No generations yet. Start creating!
                  </Typography>
                  <Button
                    variant="contained"
                    size="large"
                    onClick={() => {
                      window.scrollTo({ top: 0, behavior: 'smooth' });
                      navigate('/generate');
                    }}
                    sx={{
                      background: 'linear-gradient(90deg, #A864ED 0%, #F5B760 100%)',
                      color: 'white',
                      fontFamily: 'Montserrat, sans-serif',
                      fontWeight: 600,
                      borderRadius: '10px',
                      py: { xs: 1, sm: 1.2 },
                      px: 4,
                      textTransform: 'none',
                      fontSize: '14px',
                      boxShadow: 'none',
                      '&:hover': {
                        background: 'linear-gradient(90deg, #9C52DB 0%, #EAA24A 100%)',
                        boxShadow: 'none',
                      },
                    }}
                  >
                    Start to Generate
                  </Button>
                </Box>
              ) : (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                  {recentGenerations.map((generation, index) => (
                    <Card
                      key={generation.id}
                      elevation={0}
                      sx={{
                        border: '1px solid rgba(99, 24, 197, 0.15)',
                        borderRadius: '8.66px',
                        boxShadow: '0px 4.33px 12.98px 0px rgba(0, 0, 0, 0.08)',
                        overflow: 'hidden',
                        transition: 'border 0.6s ease-in-out, transform 0.3s ease-in-out',
                        '&:hover': {
                          border: '1px solid #6318C6',
                          transform: 'translateY(-2px)',
                        }
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', py: 1.5, px: 1.5, position: 'relative' }}>
                        {/* Left: Square thumbnail */}
                        <Box
                          sx={{
                            width: '80px',
                            height: '125px',
                            bgcolor: '#f5f5f5',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            flexShrink: 0,
                            position: 'relative',
                            overflow: 'hidden',
                            borderRadius: '6px',
                            mr: 2
                          }}
                        >
                          {/* REEL label — UI overlay (never cropped by object-fit cover) */}
                          {generation.jewelry_category?.display_name === 'Reel' && (
                            <Box sx={{
                              position: 'absolute', top: 4, left: 4, zIndex: 5, pointerEvents: 'none',
                              display: 'flex', alignItems: 'center', gap: '2px', px: '5px', py: '2px',
                              background: 'linear-gradient(90deg, #A864ED 0%, #7B3FE4 100%)',
                              borderRadius: '3px 9px 9px 3px',
                              boxShadow: '0 1px 3px rgba(0,0,0,0.30)',
                            }}>
                              <PlayArrow sx={{ color: '#fff', fontSize: 10 }} />
                              <Typography sx={{ color: '#fff', fontSize: 8, fontWeight: 700, lineHeight: 1, letterSpacing: '0.3px' }}>
                                REEL
                              </Typography>
                            </Box>
                          )}
                          {generation.status?.name === 'COMPLETED' && generation.output_format?.name === 'VIDEO' ? (
                            // Video thumbnail - use enhanced image if available, otherwise show generic video placeholder
                            generation.enhanced_image_path ? (
                              // Video thumbnail with enhanced image and play button overlay
                              <Box sx={{ position: 'relative', width: '100%', height: '100%' }}>
                                <SecureImage
                                  src={getSecureThumbnailUrl(generation.id, '400x400', 'enhanced')}
                                  alt="video preview"
                                  useThumbnail={true}
                                  thumbnailSize="400x400"
                                  style={{
                                    width: '100%',
                                    height: '100%',
                                    objectFit: 'cover',
                                    borderRadius: '6px'
                                  }}
                                  onError={(e) => {
                                    e.target.onerror = null;
                                    e.target.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNDgiIGhlaWdodD0iNDgiIHZpZXdCb3g9IjAgMCA0OCA0OCIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KPHJlY3Qgd2lkdGg9IjQ4IiBoZWlnaHQ9IjQ4IiBmaWxsPSIjRjNGNEY2Ii8+CjxwYXRoIGQ9Ik0xNiAxNkgyNFYyNEgxNlYxNloiIGZpbGw9IiM5Q0EzQUYiLz4KPHBhdGggZD0iTTE2IDI4SDI0VjM2SDE2VjI4WiIgZmlsbD0iIjlDQTNBRiIvPgo8cGF0aCBkPSJNMjggMTZIMzZWMjRIMjhWMTZaIiBmaWxsPSIjOUNBM0FGIi8+CjxwYXRoIGQ9Ik0yOCAyOEgzNlYzNkgyOFYyOFoiIGZpbGw9IiM5Q0EzQUYiLz4KPC9zdmc+';
                                  }}
                                />
                                {/* Play button overlay */}

                                <Box
                                  sx={{
                                    position: 'absolute',
                                    top: '50%',
                                    left: '50%',
                                    transform: 'translate(-50%, -50%)',
                                    bgcolor: 'rgba(0, 0, 0, 0.6)',
                                    borderRadius: '50%',
                                    width: '32px',
                                    height: '32px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center'
                                  }}
                                >
                                  <PlayArrow sx={{ color: 'white', fontSize: 20 }} />
                                </Box>
                              </Box>
                            ) : (
                              // Generic video placeholder when no enhanced image is available
                              <Box
                                sx={{
                                  width: '100%',
                                  height: '100%',
                                  bgcolor: '#1e293b',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  position: 'relative'
                                }}
                              >
                                <PlayArrow
                                  sx={{
                                    color: 'white',
                                    fontSize: 32,
                                    position: 'absolute',
                                    top: '50%',
                                    left: '50%',
                                    transform: 'translate(-50%, -50%)'
                                  }}
                                />
                                <Typography
                                  variant="caption"
                                  sx={{
                                    color: 'white',
                                    fontSize: 10,
                                    position: 'absolute',
                                    bottom: 4,
                                    left: '50%',
                                    transform: 'translateX(-50%)',
                                    fontWeight: 600
                                  }}
                                >
                                  VIDEO
                                </Typography>
                              </Box>
                            )
                          ) : generation.status?.name === 'COMPLETED' ? (
                            // Image thumbnail
                            <SecureImage
                              src={getSecureThumbnailUrl(generation.id, '400x400')}
                              alt="preview"
                              useThumbnail={true}
                              thumbnailSize="400x400"
                              style={{
                                width: '100%',
                                height: '100%',
                                objectFit: 'cover',
                                borderRadius: '6px'
                              }}
                              onError={(e) => {
                              }}
                            />
                          ) : generation.status?.name === 'FAILED' ? (
                            // Failed generation placeholder
                            <Box
                              sx={{
                                width: '100%',
                                height: '100%',
                                bgcolor: 'grey.100',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                opacity: 0.5
                              }}
                            >
                              <ImageIcon sx={{ color: '#9ca3af', fontSize: 32 }} />
                            </Box>
                          ) : (
                            // Loading/processing placeholder
                            <Box
                              sx={{
                                width: '100%',
                                height: '100%',
                                bgcolor: '#f3f4f6',
                                opacity: 0.5,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center'
                              }}
                            >
                              <CircularProgress size={24} sx={{ color: '#6318C6' }} />
                            </Box>
                          )}
                        </Box>

                        {/* Center: Main content */}
                        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 0.5, position: 'relative' }}>
                          {/* Title */}
                          <Typography
                            variant="subtitle1"
                            sx={{
                              fontFamily: 'Montserrat, sans-serif',
                              fontWeight: 600,
                              color: '#1e293b',
                              fontSize: { xs: '14px', sm: '16px' },
                              lineHeight: 1.2,
                              mb: 3
                            }}
                          >
                            {generation.jewelry_category?.display_name || 'Category'}
                          </Typography>

                          {/* Status badge */}
                          {generation.status?.name === 'COMPLETED' && (
                            <Chip
                              label="Completed"
                              size="small"
                              sx={{
                                bgcolor: '#ECFDF5',
                                color: '#059669',
                                fontWeight: 500,
                                fontFamily: 'Montserrat, sans-serif',
                                borderRadius: '13.15px',
                                fontSize: '12px',
                                height: 24,
                                border: '1px solid #6EE7B7',
                                width: 'fit-content'
                              }}
                            />
                          )}
                          {(generation.status?.name === 'PROCESSING' || generation.status?.name === 'PENDING') && (
                            <Chip
                              label="Processing"
                              size="small"
                              sx={{
                                bgcolor: '#F5F3FF',
                                color: '#7C3AED',
                                fontWeight: 500,
                                fontFamily: 'Montserrat, sans-serif',
                                borderRadius: '13.15px',
                                fontSize: '12px',
                                height: 24,
                                border: '1px solid #7C3AED',
                                width: 'fit-content'
                              }}
                            />
                          )}
                          {generation.status?.name === 'FAILED' && (
                            <Chip
                              label="Failed"
                              size="small"
                              sx={{
                                bgcolor: '#FEF2F2',
                                color: '#DC2626',
                                fontWeight: 500,
                                fontFamily: 'Montserrat, sans-serif',
                                borderRadius: '13.15px',
                                fontSize: '12px',
                                height: 24,
                                border: '1px solid #FCA5A5',
                                width: 'fit-content'
                              }}
                            />
                          )}


                          {/* Credits info at bottom left */}
                          <Typography
                            variant="body2"
                            sx={{
                              fontFamily: 'Montserrat, sans-serif',
                              fontWeight: 500,
                              fontSize: '12.98px',
                              color: '#1E2939',
                              position: 'absolute',
                              bottom: -22,
                              right: 0
                            }}
                          >
                            {FlutterServicesAPI.getCreditLabel()}: <span style={{ color: '#651BC6' }}>{generation.credits_cost}</span>
                          </Typography>
                        </Box>

                        {/* Right: Download button positioned at top right */}
                        <Box sx={{
                          display: 'flex',
                          alignItems: 'flex-start',
                          justifyContent: 'flex-end',
                          position: 'absolute',
                          top: { xs: 20, sm: 10, md: 10 },
                          right: { xs: 10, sm: 8, md: 5 }
                        }}>
                          <Tooltip title={!generation.is_downloadable ? "" : "Download this generation"} arrow placement="top">
                            <Box sx={{ position: 'relative', display: 'inline-block' }}>
                              <IconButton
                                onClick={() => handleDownload(generation)}
                                disabled={!generation.is_downloadable}
                                sx={{
                                  width: { xs: 40, sm: 65, md: 65 },
                                  height: { xs: 40, sm: 65, md: 65 },
                                  color: 'white',
                                  padding: { xs: 0, sm: 1, md: 1 },
                                  '&:disabled': {
                                    opacity: 1,
                                    bgcolor: 'transparent'
                                  }
                                }}
                              >
                                <Box
                                  component="img"
                                  src="/assets/img/icons/download-gen.svg"
                                  alt="Download"
                                  sx={{
                                    width: { xs: '100%', sm: '100%', md: '100%' },
                                    height: { xs: '100%', sm: '100%', md: '100%' }
                                  }}
                                />
                              </IconButton>
                              {!generation.is_downloadable && (
                                <Box
                                  sx={{
                                    position: 'absolute',
                                    top: { xs: 0, sm: 8, md: 8 },
                                    left: { xs: 0, sm: 8, md: 8 },
                                    width: { xs: '100%', sm: 'calc(100% - 16px)', md: 'calc(100% - 16px)' },
                                    height: { xs: '100%', sm: 'calc(100% - 16px)', md: 'calc(100% - 16px)' },
                                    backgroundColor: 'rgba(255, 255, 255, 0.5)',
                                    borderRadius: '50%',
                                    pointerEvents: 'none'
                                  }}
                                />
                              )}
                            </Box>
                          </Tooltip>
                        </Box>
                      </Box>
                    </Card>
                  ))}
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Credit Packages Section at Bottom - COMMENTED OUT FOR FUTURE USE */}
        {/* 
        <Grid item xs={12} md={12}>
        {creditsInfo?.packages && creditsInfo.packages.length > 0 && (
          <Card elevation={0} sx={{ border: '1px solid #e5e7eb', boxShadow: 'none', mt: 0 }}>
            <CardContent>
              <Typography
                variant="h6"
                fontWeight={600}
                fontSize={{ xs: "18px", sm: "20px", md: "27px" }}
                mb={2}
                display="flex"
                alignItems="center"
                gap={1}
              >
                {`Available ${FlutterServicesAPI.getCreditLabel()} Packages`}
              </Typography>
              <Grid container spacing={3} justifyContent="flex-start" alignItems="stretch" mb={2}>
                {creditsInfo.packages.map((pkg) => {
                  const isPopular = pkg.is_popular;
                  return (
                    <Grid item xs={12} sm={6} md={4} lg={3} key={pkg.id} display="flex" justifyContent="center">
                      <Box position="relative" display="flex" flexDirection="column" alignItems="center" width="100%" maxWidth={320}>
                        {/* Popular badge */}
        {/* {isPopular && (
                          <Box position="absolute" top={-18} left="50%" sx={{ transform: 'translateX(-50%)' }} zIndex={2}>
                            <Box px={2} py={0.5} bgcolor="#a259f7" color="white" borderRadius={2} fontWeight="bold" fontSize={14} boxShadow={2}>
                              Popular
                            </Box>
                          </Box>
                        )} */}
        {/* <Card
                          elevation={isPopular ? 6 : 2}
                          sx={{
                            width: '100%',
                            minWidth: 220,
                            maxWidth: 320,
                            minHeight: 240,
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'flex-start',
                            pt: isPopular ? 3 : 4,
                            border: isPopular ? '2.5px solid #a259f7' : '2px solid #e5e7eb',
                            boxShadow: isPopular ? '0 0 0 2px #a259f7' : '0 0 0 1.5px #e5e7eb',
                            borderRadius: 3,
                            position: 'relative',
                            transition: 'transform 0.2s, box-shadow 0.2s, border-color 0.2s',
                            '&:hover': {
                              transform: 'translateY(-4px) scale(1.03)',
                              boxShadow: isPopular ? '0 0 0 4px #a259f7' : '0 0 0 2.5px #a259f7',
                              borderColor: '#a259f7',
                            },
                          }}
                        >
                          <CardContent sx={{ textAlign: 'center', flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center', p: 4 }}>
                            <Typography variant="h6" fontWeight="bold" gutterBottom>
                              {pkg.name}
                            </Typography>
                            <Typography variant="h4" fontWeight="bold" color="#a259f7" gutterBottom>
                              ₹{pkg.price}
                            </Typography>
                            <Typography variant="body1" color="text.secondary" mb={2}>
                              {formatCredits(pkg.credits)} {FlutterServicesAPI.getCreditLabel()}
                              {pkg.bonus_credits > 0 && (
                                <span style={{ color: '#4caf50', fontWeight: 600 }}> (+{formatCredits(pkg.bonus_credits)} bonus)</span>
                              )}
                            </Typography>
                            <Box flexGrow={1} />
                            <Button
                              variant="contained"
                              fullWidth
                              size="medium"
                              onClick={() => handlePurchasePackage(pkg)}
                              sx={{
                                background: '#0d1321',
                                color: 'white',
                                fontWeight: 'bold',
                                fontSize: 14,
                                py: 1.2,
                                borderRadius: 1.5,
                                boxShadow: 'none',
                                textTransform: 'capitalize',
                                '&:hover': {
                                  background: '#23293a',
                                  color: 'white',
                                },
                              }}
                            >
                              Purchase
                            </Button>
                          </CardContent>
                        </Card>
                      </Box>
                    </Grid>
                  );
                })}
              </Grid>
            </CardContent>
          </Card>
        )}
        </Grid>
        */}

        {/* Request Credits Section */}
        {canPurchaseCredits && !FlutterServicesAPI.isFlutterApp() && (
          <Grid className="ai-model-studio" item xs={12} md={6} sx={{ height: '100%', pt: { xs: 1, md: 2 } }}>
            <Card elevation={0} sx={{
              border: 'none',
              boxShadow: '0px 4.33px 12.98px 0px rgba(0, 0, 0, 0.08)',
              borderRadius: '17.31px',
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center'
            }}>
          {showWhatsAppSmsAuth && (
            <CardContent sx={{ textAlign: 'center', py: { xs: 3, sm: 4 } }}>
                <Typography
                  variant="h6"
                  sx={{
                    fontFamily: 'Montserrat, sans-serif',
                    fontWeight: 600,
                    fontSize: '17.31px',
                    color: '#1E2939',
                    mb: 1,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 1
                  }}
                >
                  Need More {FlutterServicesAPI.getCreditLabel()}?
                </Typography>
                <Typography sx={{ fontFamily: 'Montserrat, sans-serif', fontWeight: 400, color: '#000000', fontSize: '12.68px', mb: 3 }}>
                  Request custom {FlutterServicesAPI.getCreditLabel().toLowerCase()} packages tailored to your business needs
                </Typography>
                <Button
                  variant="contained"
                  onClick={() => navigate('/credits?tab=0')}
                  sx={{
                    background: 'linear-gradient(90deg, #A864ED 0%, #F5B760 100%)',
                    color: 'white',
                    fontFamily: 'Montserrat, sans-serif',
                    fontWeight: 600,
                    borderRadius: '10px',
                    py: { xs: 1, sm: 1.2 },
                    px: 4,
                    textTransform: 'none',
                    fontSize: '14px',
                    boxShadow: 'none',
                    '&:hover': {
                      background: 'linear-gradient(90deg, #9C52DB 0%, #EAA24A 100%)',
                      boxShadow: 'none',
                    },
                  }}
                >
                  Request {FlutterServicesAPI.getCreditLabel()}
                </Button>
              </CardContent>
            )}
            </Card>
          </Grid>
        )}
      </Grid>

      {/* Request Confirmation Dialog */}
      <Dialog open={purchaseDialog} onClose={() => setPurchaseDialog(false)}>
        <DialogTitle>Confirm Request</DialogTitle>
        <DialogContent>
          {selectedPackage && (
            <Card
              elevation={2}
              sx={{
                width: 260,
                minHeight: 240,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'flex-start',
                pt: 4,
                border: '2px solid #e5e7eb',
                boxShadow: '0 0 0 1.5px #e5e7eb',
                borderRadius: 3,
                position: 'relative',
                mb: 2,
                mx: 'auto',
                background: '#fff',
              }}
            >
              <CardContent sx={{ textAlign: 'center', flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center', p: 4 }}>
                <Typography variant="h6" fontWeight="bold" gutterBottom>
                  {selectedPackage.name}
                </Typography>
                <Typography variant="h4" fontWeight="bold" color="#a259f7" gutterBottom>
                  ₹{selectedPackage.price}
                </Typography>
                <Typography variant="body1" color="text.secondary" mb={2}>
                  {formatCredits(selectedPackage.credits)} {FlutterServicesAPI.getCreditLabel()}
                  {selectedPackage.bonus_credits > 0 && (
                    <span style={{ color: '#4caf50', fontWeight: 600 }}> (+{formatCredits(selectedPackage.bonus_credits)} bonus)</span>
                  )}
                </Typography>
              </CardContent>
            </Card>
          )}
          {purchaseError && <Alert severity="error" sx={{ mt: 2 }}>{purchaseError}</Alert>}
        </DialogContent>
        <DialogActions>
          <Button
            variant="outlined"
            startIcon={<Close />}
            onClick={() => setPurchaseDialog(false)}
            sx={{ mr: 1, borderColor: 'black', color: 'black', '&:hover': { borderColor: 'black', backgroundColor: '#f3f4f6', color: 'black' } }}
          >
            CANCEL
          </Button>
          <Button
            variant="contained"
            onClick={confirmPurchase}
            disabled={purchaseLoading}
            sx={{
              background: 'linear-gradient(90deg, #A864ED 0%, #F5B760 100%)',
              color: 'white',
              fontWeight: 'bold',
              fontFamily: 'Montserrat, sans-serif',
              px: 4,
              textTransform: 'capitalize',
              borderRadius: 1.5,
              boxShadow: 'none',
              '&:hover': {
                background: 'linear-gradient(90deg, #9C52DB 0%, #EAA24A 100%)',
                color: 'white',
                boxShadow: 'none',
              },
            }}
          >
            {purchaseLoading ? 'Processing...' : 'Confirm Request'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Custom Credit Request Dialog - COMMENTED OUT: Now redirects to CreditsManagement page instead */}
      {/* 
      <Dialog open={customRequestDialog} onClose={handleCancelCustomRequest} maxWidth="xs" fullWidth>
        <DialogTitle>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            {`Request Custom ${FlutterServicesAPI.getCreditLabel()}`}
            <IconButton onClick={handleCancelCustomRequest} size="small">
              <Close />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" gutterBottom sx={{ mb: 2 }}>
            {`Request a custom ${FlutterServicesAPI.getCreditLabel().toLowerCase()} amount for your business needs. Minimum 100 ${FlutterServicesAPI.getCreditLabel().toLowerCase()}, maximum ${maxCreditLimit.toLocaleString()} ${FlutterServicesAPI.getCreditLabel().toLowerCase()}.`}
          </Typography>
          <TextField
            fullWidth
            label={`${FlutterServicesAPI.getCreditLabel()} Required *`}
            type="number"
            value={customRequest.credits}
            onChange={(e) => {
              const value = e.target.value;
              setCustomRequest(prev => ({ ...prev, credits: value }));
              
              // Real-time validation
              const error = validateCreditAmount(value);
              setValidationError(error);
              setPurchaseError(''); // Clear previous errors
            }}
            placeholder={`Enter ${FlutterServicesAPI.getCreditLabel().toLowerCase()} between 100 and ${maxCreditLimit.toLocaleString()}`}
            inputProps={{ 
              min: 100, 
              max: maxCreditLimit 
            }}
            error={!!validationError}
            helperText={validationError || `Enter ${FlutterServicesAPI.getCreditLabel().toLowerCase()} between 100 and ${maxCreditLimit.toLocaleString()}`}
            sx={{ mb: 2 }}
          />

          <TextField
            fullWidth
            label="Reason/Notes (Optional)"
            multiline
            rows={3}
            value={customRequest.notes}
            onChange={(e) => setCustomRequest(prev => ({ ...prev, notes: e.target.value }))}
            placeholder={`Please provide a reason for your custom ${FlutterServicesAPI.getCreditLabel().toLowerCase()} request`}
            helperText={`Explain why you need custom ${FlutterServicesAPI.getCreditLabel().toLowerCase()} (business purpose, project details, etc.)`}
            sx={{
              '& .MuiOutlinedInput-root': {
                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                  borderColor: '#6318C6',
                },
                '&:hover .MuiOutlinedInput-notchedOutline': {
                  borderColor: '#6318C6',
                }
              },
              '& .MuiInputLabel-root.Mui-focused': {
                color: '#6318C6',
              },
              '& .MuiInputBase-input::placeholder': {
                fontSize: '13px',
                opacity: 0.7
              }
            }}
          />
          
          {(validationError || purchaseError) && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {validationError || purchaseError}
            </Alert>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 3, pt: 1 }}>
          <Button
            onClick={handleCancelCustomRequest}
            disabled={purchaseLoading}
            sx={{
              textTransform: 'none',
              color: 'black'
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleCustomCreditRequest}
            variant="contained"
            disabled={purchaseLoading || !customRequest.credits || !!validationError}
            sx={{
              background: purchaseLoading || !customRequest.credits || parseInt(customRequest.credits) < 100 ? '#E0E0E0' : '#6318C6',
              color: 'white',
              textTransform: 'none',
              '&:hover': {
                background: purchaseLoading || !customRequest.credits || parseInt(customRequest.credits) < 100 ? '#E0E0E0' : '#6318C6',
              },
              '&:disabled': {
                background: '#E0E0E0',
                color: '#424242'
              }
            }}
          >
            {purchaseLoading ? (
              <>
                <CircularProgress size={16} color="inherit" sx={{ mr: 1 }} />
                Submitting...
              </>
            ) : (
              'Submit Request'
            )}
          </Button>
        </DialogActions>
      </Dialog>
      */}

      {/* Beautiful Success Dialog */}
      <Dialog
        open={successDialog}
        onClose={handleCloseSuccessDialog}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            background: 'white',
            color: '#374151', // Changed to dark color for better contrast on white
            overflow: 'visible'
          }
        }}
      >
        <DialogTitle sx={{ position: 'relative', textAlign: 'center', pb: 1 }}>
          <IconButton
            onClick={handleCloseSuccessDialog}
            sx={{
              position: 'absolute',
              right: 8,
              top: 8,
              color: '#374151', // Changed to dark color
              '&:hover': { background: 'rgba(0,0,0,0.1)' }
            }}
          >
            <Close />
          </IconButton>
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mt: 2 }}>
            <Box
              sx={{
                background: 'rgba(99, 24, 198, 0.1)', // Changed to light purple background
                borderRadius: '50%',
                p: 2,
                mb: 2,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
            >
              <CheckCircle sx={{ fontSize: 48, color: '#4CAF50' }} />
            </Box>
            <Typography variant="h5" fontWeight="bold" gutterBottom sx={{ color: '#374151' }}>
              Success!
            </Typography>
            <Typography variant="h6" sx={{ opacity: 0.9, textAlign: 'center', color: '#374151' }}>
              {successMessage}
            </Typography>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ pt: 0, pb: 2 }}>
          <Box sx={{ textAlign: 'center' }}>
            {requestDetails && (
              <Card sx={{ mt: 1, background: 'transparent', color: '#374151', boxShadow: 'none' }}>
                <CardContent sx={{ py: 1 }}>
                  <Grid container spacing={1}>
                    {/* Combined package name and credits into one line */}
                    <Grid item xs={12}>
                      <Box display="flex" alignItems="center" justifyContent="center" gap={1}>
                        <Typography variant="body1" fontWeight={600} sx={{ color: '#374151' }}>
                          {`Requested ${FlutterServicesAPI.getCreditLabel()} - ${requestDetails.credits}`}
                        </Typography>
                      </Box>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            )}

            <Typography variant="body2" sx={{ mt: 1, opacity: 0.8, color: '#374151' }}>
              You will be notified once your request is processed.
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions sx={{ justifyContent: 'center', pb: 3 }}>
          <Button
            onClick={handleCloseSuccessDialog}
            variant="contained"
            size="large"
            sx={{
              background: 'linear-gradient(90deg, #A864ED 0%, #F5B760 100%)',
              color: 'white !important',
              fontWeight: 'bold',
              fontFamily: 'Montserrat, sans-serif',
              minHeight: '50px',
              height: '50px',
              textTransform: 'none',
              fontSize: '16px',
              px: 4,
              boxShadow: 'none',
              '&:hover': {
                background: 'linear-gradient(90deg, #9C52DB 0%, #EAA24A 100%)',
                boxShadow: 'none',
              }
            }}
          >
            Continue
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default memo(MerchantDashboardContent); 